import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Plus, 
  Search, 
  BookOpen, 
  Clock, 
  MoreHorizontal,
  ChevronRight,
  Monitor,
  Users,
  LayoutGrid
} from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import TeacherClass from './TeacherClass';

interface Class {
  id: string;
  name: string;
  subject: string;
  studentCount: number;
  schedule: string;
  room?: string;
  status: 'active' | 'upcoming' | 'completed';
}

const mockClasses: Class[] = [
  { id: '1', name: 'Biology 101', subject: 'Science', studentCount: 24, schedule: 'Mon, Wed 10:00 AM', room: 'Lab A', status: 'active' },
  { id: '2', name: 'Advanced Calculus', subject: 'Mathematics', studentCount: 18, schedule: 'Tue, Thu 02:30 PM', room: 'Room 302', status: 'active' },
  { id: '3', name: 'English Literature', subject: 'Humanities', studentCount: 30, schedule: 'Fri 09:00 AM', room: 'Online', status: 'upcoming' },
  { id: '4', name: 'World History', subject: 'History', studentCount: 22, schedule: 'Mon, Wed 01:00 PM', room: 'Room 105', status: 'active' },
];

const ClassesManagerView = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [classes] = useState<Class[]>(mockClasses);

  const filteredClasses = classes.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 w-full max-w-7xl mx-auto">
      {/* Header Area */}
      <div className="flex flex-col md:flex-row justify-between items-end gap-6 bg-white dark:bg-[#020617] p-1 border-b border-gray-100 dark:border-white/5 pb-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] text-blue-500 uppercase">
             <LayoutGrid size={12} />
             <span>Academic Management</span>
          </div>
          <h1 className="text-3xl font-light tracking-tight text-gray-900 dark:text-white font-serif italic">
            Class <span className="font-bold not-italic">Directory</span>
          </h1>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative w-64 group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <Input 
              placeholder="Filter classes..." 
              className="pl-9 h-10 bg-gray-50 dark:bg-white/5 border-none rounded-none text-xs font-medium focus-visible:ring-1 focus-visible:ring-blue-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button className="h-10 rounded-none bg-black dark:bg-white dark:text-black hover:bg-gray-800 text-[10px] font-bold tracking-widest uppercase px-6">
            <Plus className="mr-2 h-3.5 w-3.5" /> New Class
          </Button>
        </div>
      </div>

      {/* Meet Integration MVP */}
      <TeacherClass />

      {/* Grid Area */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
        <AnimatePresence mode="popLayout">
          {filteredClasses.map((cls) => (
            <motion.div
              key={cls.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="group relative border border-gray-100 dark:border-white/5 bg-white dark:bg-[#020617] rounded-none shadow-none hover:border-blue-500/30 transition-all duration-500 overflow-hidden">
                <CardContent className="p-0">
                   <div className="flex h-full">
                      {/* Status Strip */}
                      <div className={`w-1 transition-all duration-500 ${cls.status === 'active' ? 'bg-blue-500 group-hover:w-2' : 'bg-gray-200 dark:bg-white/10'}`} />
                      
                      <div className="flex-1 p-5 space-y-5">
                         {/* Top Row: Meta */}
                         <div className="flex justify-between items-start">
                            <div className="space-y-1">
                               <span className="text-[9px] font-black tracking-widest text-gray-400 uppercase">{cls.subject}</span>
                               <h3 className="text-lg font-bold tracking-tight group-hover:text-blue-500 transition-colors">{cls.name}</h3>
                            </div>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none opacity-0 group-hover:opacity-100 transition-opacity">
                               <MoreHorizontal size={14} />
                            </Button>
                         </div>

                         {/* Middle Row: Details */}
                         <div className="grid grid-cols-2 gap-6">
                            <div className="flex items-center gap-3 text-gray-500">
                               <div className="h-8 w-8 flex items-center justify-center bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 transition-colors group-hover:border-blue-500/20">
                                  <Clock size={14} className="group-hover:text-blue-500 transition-colors" />
                               </div>
                               <div className="space-y-0.5">
                                  <p className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter">Schedule</p>
                                  <p className="text-[10px] font-medium text-gray-900 dark:text-gray-200">{cls.schedule}</p>
                               </div>
                            </div>
                            <div className="flex items-center gap-3 text-gray-500">
                               <div className="h-8 w-8 flex items-center justify-center bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 transition-colors group-hover:border-blue-500/20">
                                  <Monitor size={14} className="group-hover:text-blue-500 transition-colors" />
                               </div>
                               <div className="space-y-0.5">
                                  <p className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter">Location</p>
                                  <p className="text-[10px] font-medium text-gray-900 dark:text-gray-200">{cls.room}</p>
                               </div>
                            </div>
                         </div>

                         {/* Bottom Row: Footer */}
                         <div className="pt-4 border-t border-gray-50 dark:border-white/5 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                               <div className="flex items-center -space-x-1.5">
                                  {[1, 2, 3].map((i) => (
                                     <div key={i} className="h-5 w-5 rounded-full border border-white dark:border-[#020617] bg-gray-100 dark:bg-white/10 flex items-center justify-center text-[8px] font-bold text-gray-500" />
                                  ))}
                               </div>
                               <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{cls.studentCount} Students</span>
                            </div>
                            
                            <Button variant="link" className="group/btn h-auto p-0 text-[10px] font-black uppercase tracking-widest text-blue-500 hover:no-underline">
                               Open Manager <ChevronRight size={12} className="ml-1 group-hover/btn:translate-x-1 transition-transform" />
                            </Button>
                         </div>
                      </div>
                   </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filteredClasses.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 border border-dashed border-gray-100 dark:border-white/5">
          <BookOpen size={32} className="text-gray-200 dark:text-white/5 mb-4" />
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">No Matches Found</h3>
        </div>
      )}
    </div>
  );
};

export default ClassesManagerView;
